<template>
  <!--
    Priority alert carousel. Shows the head of the priorityAlerts queue as a
    trade-app card (AlertCard) plus subtle "ghost" cards peeking from
    underneath to communicate that more notifications are queued. Tapping
    the active card acks it (and, for order fills, routes to the matched
    game); the corner X clears the entire priority queue.

    All priority types (orderFill, new-player, deposit-confirmed) share the
    same carousel + ghost-stack treatment for visual consistency.
  -->
  <div class="notification-container">
    <div v-if="notification" class="alert-stack">
      <!-- Subtle ghost cards behind to communicate more in the queue -->
      <div
        v-if="notificationsLength > 2"
        class="alert-stack__ghost alert-stack__ghost--far"
        aria-hidden="true"
      ></div>
      <div
        v-if="notificationsLength > 1"
        class="alert-stack__ghost alert-stack__ghost--near"
        aria-hidden="true"
      ></div>
      <transition name="alert-cycle" mode="out-in">
        <alert-card
          :key="notification.id"
          :title="cardCopy.title"
          :detail="cardCopy.detail"
          :percent="cardCopy.percent"
          :time="cardCopy.time"
          :variant="cardCopy.variant"
          clickable
          :meta-text="cardMatchup"
          :navigable="isNavigable"
          :dismiss-label="dismissLabel"
          @click="handleCardAck"
          @navigate="handleCardNavigate"
          @dismiss="clearPriorityNotifications"
        />
      </transition>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import AlertCard from '@/components/global/AlertCard.vue';
import { PostHogUtils, normalizeNotificationType } from '@/core/PostHogUtils';
import { useNotificationStore } from '@/stores/notification';

const notificationStore = useNotificationStore();
const router = useRouter();
const { priorityAlerts } = storeToRefs(notificationStore);

const notificationsLength = computed(() => priorityAlerts.value.length);
const notification = computed(() => priorityAlerts.value[0]);

// Per-type derivation of the card copy. Each priority alert type wants
// slightly different framing (label, sub-line formatting, variant). We
// resolve all of that here so the template is just a dumb AlertCard host.
const cardCopy = computed(() => {
  const n = notification.value;
  if (!n) return { title: '', detail: '', time: '', variant: 'neutral' };

  switch (n.type) {
    case 'orderFill': {
      const progress = parseFillProgress(n.detail);
      return {
        // Put the fill percent right in the title so progress is the headline —
        // "Order 13% Filled" for partials, "Order 100% Filled" when complete.
        // Falls back to the generic "Order Filled" only when the backend didn't
        // ship a parseable fraction (e.g. the "<1" small-amount edge).
        title:
          progress && progress.percent != null
            ? `Order ${progress.percent}% Filled`
            : 'Order Filled',
        // Backend ships line 1 as "<side> <odds> $<risk> (<filled>/<total>)"
        // (core-api src/core/utils.js getNotificationString). The bare
        // "(12/200)" reads like a risk/win pair, which is confusing. We drop
        // it and replace the per-fill "$<risk>" with the cumulative filled
        // dollars so the line reads "Bears -7 +110 · $12" (filled-to-date).
        detail: buildFillBetLine(n.detail, progress),
        // Percent now lives in the title, so no separate chip.
        percent: null,
        time: formatFillTime(n.createdAt),
        // Priority/order-fill toasts deliberately render without an accent
        // stripe — the carousel + timestamp already mark them as distinct
        // from the regular alert stack.
        variant: 'neutral',
      };
    }
    case 'new-player':
      return {
        title: n.title || 'New Player',
        detail: (n.detail || '').split('\n')[0] || '',
        time: formatFillTime(n.createdAt),
        variant: 'neutral',
      };
    case 'deposit-confirmed':
      return {
        title: n.title || 'Deposit Confirmed',
        detail: (n.detail || '').split('\n')[0] || '',
        time: formatFillTime(n.createdAt),
        variant: 'neutral',
      };
    default:
      return {
        title: n.title || '',
        detail: (n.detail || '').split('\n')[0] || '',
        time: formatFillTime(n.createdAt),
        variant: 'neutral',
      };
  }
});

const dismissLabel = computed(() => {
  return notificationsLength.value > 1
    ? `Dismiss all ${notificationsLength.value} notifications`
    : 'Dismiss notification';
});

// Only order fills with a known game are navigable. When navigable the title
// gains a trailing arrow and the title + matchup read as the affordance (hover
// underline); the click bubbles to handleCardClick for ack + route.
const isNavigable = computed(() => {
  const n = notification.value;
  return Boolean(n && n.type === 'orderFill' && n.gameID);
});

// Totals are the only fills whose detail line is ambiguous on its own —
// "Over 220.5" doesn't say which game — so the matchup is only surfaced for
// totals. Other markets name the team in the detail line already.
//
// Detection: line 1 is "<side> <odds> $<risk>" (helpers/order_fill_notification.go).
// Strip any leading rotation number, then a total reads "Over .." / "Under ..".
const isTotalFill = computed(() => {
  const n = notification.value;
  if (!n || n.type !== 'orderFill') return false;
  const betSide = (n.detail || '')
    .split('\n')[0]
    .replace(/^\d+\s+/, '')
    .trim();
  return /^(over|under)\b/i.test(betSide);
});

// Backend ships the matchup as line 2: "AWAY @ HOME | <date>". Pull line 2,
// drop the trailing "| <date>", and strip any leading rotation numbers from
// each side ("501 LAL @ 502 BOS" -> "LAL @ BOS"). Totals only.
const cardMatchup = computed(() => {
  const n = notification.value;
  if (!n || n.type !== 'orderFill' || !isTotalFill.value) return '';
  const line2 = (n.detail || '').split('\n')[1] || '';
  const matchup = line2.split('|')[0].trim();
  return matchup
    .split(' @ ')
    .map((side) => side.replace(/^\d+\s+/, '').trim())
    .join(' @ ');
});

// Backend line 1 ends with a "(<filled>/<total>)" fill fraction in maker-risk
// dollars, e.g. "Bears -7 +110 $28 (12/200)" (core-api getNotificationString,
// formatFillCount can also emit "<1"). Pull those two numbers so we can show a
// clean "$X of $Y offer filled" line and a percent chip instead of the raw,
// risk/win-looking parenthetical. Returns null when no fraction is present.
function parseFillProgress(detail) {
  const line1 = (detail || '').split('\n')[0];
  const match = line1.match(
    /\(\s*(<?\d+(?:\.\d+)?)\s*\/\s*(<?\d+(?:\.\d+)?)\s*\)\s*$/,
  );
  if (!match) return null;
  const filled = match[1];
  const total = match[2];
  // "<1" is a display sentinel ("less than a dollar"), not a real number — if
  // either side is bucketed that way we can't compute an honest percentage, so
  // leave percent null and let the title fall back to the generic "Order
  // Filled". (parseFloat would otherwise read "<1" as 1 and emit a bogus %.)
  const isBucketed = filled.startsWith('<') || total.startsWith('<');
  const filledNum = parseFloat(filled);
  const totalNum = parseFloat(total);
  let percent = null;
  if (
    !isBucketed &&
    Number.isFinite(filledNum) &&
    Number.isFinite(totalNum) &&
    totalNum > 0
  ) {
    if (filledNum >= totalNum) {
      percent = 100;
    } else {
      // Clamp a genuine partial to [1, 99] so a tiny fill never rounds down to
      // "0% Filled" and a near-complete one never rounds up to a misleading
      // "100% Filled" while the order is still open.
      percent = Math.min(
        99,
        Math.max(1, Math.round((filledNum / totalNum) * 100)),
      );
    }
  }
  return { filled, total, percent };
}

// Build the bet line shown on the toast: "<side> <odds> · $<filled>".
// We start from the backend line 1 ("Bears -7 +110 $28 (12/200)"), drop the
// trailing fraction and the per-fill "$28", then re-append the *cumulative*
// filled dollars (the "12" from "(12/200)") so the line states how much of the
// offer has filled to date. The percent chip carries the proportion. When the
// backend didn't include a parseable fraction we leave the line untouched
// (minus the usual middot styling) so nothing breaks.
function buildFillBetLine(detail, progress) {
  const line1 = (detail || '').split('\n')[0];
  const sideAndOdds = line1
    .replace(/\s*\(\s*<?\d+(?:\.\d+)?\s*\/\s*<?\d+(?:\.\d+)?\s*\)\s*$/, '')
    .replace(/\s+(<\$1|\$\d+(?:\.\d+)?)\s*$/, '')
    .trim();
  if (!progress) {
    // No fraction to work with — keep the original line, just add the middot.
    return line1.replace(/\s+\$/, ' · $');
  }
  // progress.filled is already a display string ("12" or "<1"); prefix the $.
  const filledDollars = progress.filled.startsWith('<')
    ? `<$1`
    : `$${progress.filled}`;
  return `${sideAndOdds} · ${filledDollars}`;
}

function formatFillTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// A stable "which order" key for an order-fill alert, derived purely from the
// alert text we already have (no sessionID on the alert yet). It's the bet
// side+odds line with the trailing $ amount and (x/y) fraction stripped, joined
// with the matchup line so two identical bets on different games don't collapse.
// e.g. "bears -7 +110||lal @ bos". Returns '' for non-fills / unparseable text.
function fillOrderKey(alert) {
  if (!alert || alert.type !== 'orderFill') return '';
  const lines = (alert.detail || '').split('\n');
  // Strip a leading rotation number ("341 Bears -7" -> "Bears -7"), the trailing
  // $ amount and the (x/y) fraction. Rotation numbers are stripped to match
  // isTotalFill/cardMatchup and so the key is identical across an order's fills
  // regardless of the user's rotation-number display setting.
  const sideOdds = (lines[0] || '')
    .replace(/^\d+\s+/, '')
    .replace(/\s*\(\s*<?\d+(?:\.\d+)?\s*\/\s*<?\d+(?:\.\d+)?\s*\)\s*$/, '')
    .replace(/\s+(<\$1|\$\d+(?:\.\d+)?)\s*$/, '')
    .trim()
    .toLowerCase();
  if (!sideOdds) return '';
  // Line 2 is "AWAY @ HOME | <date>"; drop the date so re-fills (different time)
  // still match, and strip leading rotation numbers from each side
  // ("501 LAL @ 502 BOS" -> "lal @ bos"). Game-level disambiguation only.
  const matchup = (lines[1] || '')
    .split('|')[0]
    .split(' @ ')
    .map((side) => side.replace(/^\d+\s+/, '').trim())
    .join(' @ ')
    .toLowerCase();
  return `${sideOdds}||${matchup}`;
}

// A click anywhere on the card body acks the active fill AND every other queued
// fill for the SAME order (matched by bet line + matchup), regardless of how
// much is filled — acking one fill on an order clears the rest of that order's
// toasts in a single batch. Falls back to acking just the clicked alert for
// non-fills or when no order key can be derived. Navigation is handled
// separately (handleCardNavigate).
function handleCardAck() {
  const n = notification.value;
  if (!n) return;
  // This carousel is where the clickable toasts actually live (orderFill,
  // deposit-confirmed, new-player); the plain toast stack is almost entirely
  // non-interactive. `type` is the notification's own stable kind — no message
  // text goes on the event. Normalized so camelCase socket names and
  // kebab-case names land as one convention.
  PostHogUtils.capture('notification_clicked', {
    notification_type: normalizeNotificationType(n.type),
    action: 'dismissed',
  });

  const key = fillOrderKey(n);
  if (key) {
    const sameOrderIds = priorityAlerts.value
      .filter((a) => fillOrderKey(a) === key)
      .map((a) => a.id);
    // Batch-ack the whole group (includes n) in one request.
    notificationStore.markInboxNotificationsAsRead(sameOrderIds, {
      updateBackend: true,
    });
    return;
  }

  notificationStore.markInboxNotificationAsRead(n.id, {
    updateBackend: true,
  });
}

// Fired only when the user clicks the navigable title/matchup text on an
// orderFill toast. Route to the matched-order's single-game view, then ack the
// whole order's fills (same grouping as handleCardAck). Navigate BEFORE acking
// so a slow/failed ack can't swallow the route.
function handleCardNavigate() {
  const n = notification.value;
  if (!n) return;
  // Captured before the route push below, so navigating away can't abandon it.
  PostHogUtils.capture('notification_clicked', {
    notification_type: normalizeNotificationType(n.type),
    action: 'opened',
  });
  if (n.type === 'orderFill' && n.gameID) {
    router.push(`/exchange-single/${n.gameID}`).catch(() => {});
  }
  const key = fillOrderKey(n);
  if (key) {
    const sameOrderIds = priorityAlerts.value
      .filter((a) => fillOrderKey(a) === key)
      .map((a) => a.id);
    notificationStore.markInboxNotificationsAsRead(sameOrderIds, {
      updateBackend: true,
    });
    return;
  }
  notificationStore.markInboxNotificationAsRead(n.id, {
    updateBackend: true,
  });
}

// Snapshot the IDs currently queued as priority toasts BEFORE we mutate the
// list. We only want to ack notifications the user is actually dismissing
// here — never unrelated unread inbox items. The batch helper sends one
// /user/ackNotifications POST instead of N.
function clearPriorityNotifications() {
  const queuedIds = priorityAlerts.value.map((a) => a.id);
  notificationStore.clearPriorityAlerts();
  notificationStore.markInboxNotificationsAsRead(queuedIds, {
    updateBackend: true,
  });
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.notification-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

// Wrapper that holds the active toast plus the subtle "ghost" cards
// peeking from underneath to communicate that more notifications are queued.
.alert-stack {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 0.25rem;
}

.alert-stack__ghost {
  position: absolute;
  border-radius: 12px;
  background: #f8f8fb;
  border: 1px solid rgba(39, 45, 88, 0.06);
  pointer-events: none;
  z-index: 0;

  [theme='night'] & {
    background: #2a3052;
    border-color: rgba(150, 156, 203, 0.1);
  }
}

// Ghost cards: communicate "more queued" without reading as muddy. Same
// surface tone as the active toast; opacity + soft shadow do the depth work.
.alert-stack__ghost--near {
  left: 10px;
  right: 10px;
  top: 6px;
  bottom: -8px;
  opacity: 0.6;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
}

.alert-stack__ghost--far {
  left: 20px;
  right: 20px;
  top: 12px;
  bottom: -16px;
  opacity: 0.32;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.05);
}

// Slide-cycle transition for the active toast: when the current alert is
// dismissed/clicked through, the next one slides into place.
.alert-cycle-enter-active {
  transition:
    transform 0.22s ease-out,
    opacity 0.22s ease-out;
}
.alert-cycle-leave-active {
  transition:
    transform 0.16s ease-in,
    opacity 0.16s ease-in;
}
.alert-cycle-enter-from {
  opacity: 0;
  transform: translateX(24px) scale(0.97);
}
.alert-cycle-leave-to {
  opacity: 0;
  transform: translateX(-16px) scale(0.97);
}
</style>
